import "moment/locale/es";

import React, { ComponentProps, ReactNode } from "react";
import { Div, Icon, IconText, Section } from "trunx";
import {
  LeafletPanelBody,
  LeafletSensorPanelHeading,
} from "../shared/LeafletPanel";

import {
  faExclamationTriangle,
  faExternalLinkAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { observer } from "mobx-react-lite";
import moment from "moment";
import Moment from "react-moment";
import config from "../config";
import { useLocaleContext } from "../contexts/LocaleContext";
import { useRootStore } from "../models/Root";
import {
  ISelectedSensor,
  SelectedPrecipSensor,
} from "../models/SelectedSensor";
import Chart from "../panes/Chart";
import PanelInfoTable from "../shared/PanelInfoTable";
import PrecipDataTable from "./SensorInfo/PrecipDataTable";
import StreamDataTable from "./SensorInfo/StreamDataTable";

interface SensorInfoPanelProps {
  selected: ISelectedSensor;
}

const SensorInfoPanel = ({ selected }: SensorInfoPanelProps) => {
  const {
    lang,
    dict: {
      panel: {
        sensors: { info: dict },
      },
    },
  } = useLocaleContext();

  const { sensor } = selected;

  moment.relativeTimeThreshold("s", 60);
  moment.relativeTimeThreshold("ss", 1);
  moment.locale(lang);
  const {
    sensors: { setSensor },
  } = useRootStore();

  const rows: [ReactNode | string, ReactNode | string][] = [];

  if (sensor.humanId) {
    rows.push([dict.ID, sensor.humanId]);
  }

  rows.push([dict.operatedBy, sensor.owner]);

  const latest = sensor.latestDate;

  if (latest === null) {
    rows.push([
      <span className="icon-text">
        <Icon>
          <FontAwesomeIcon icon={faExclamationTriangle} />
        </Icon>
        <span>{dict.missing}&nbsp;</span>
      </span>,
      null,
    ]);
  } else {
    rows.push([
      dict.lastReported,
      <span title={moment(latest).format("dddd, MMMM Do YYYY, h:mm:ss a")}>
        <Moment fromNow>{latest}</Moment>
      </span>,
    ]);
  }

  const TabularDataAnchor = (props: React.HTMLProps<HTMLAnchorElement>) => (
    <a {...props}>
      <IconText>
        {dict.recentData}
        <Icon className={"is-right"}>
          <FontAwesomeIcon icon={faExternalLinkAlt}></FontAwesomeIcon>
        </Icon>
      </IconText>
    </a>
  );

  const ChartAnchor = (props: ComponentProps<"a">) => (
    <a {...props}>
      <IconText>
        {dict.chart}
        <Icon className={"is-right"}>
          <FontAwesomeIcon icon={faExternalLinkAlt}></FontAwesomeIcon>
        </Icon>
      </IconText>
    </a>
  );

  return (
    <Div bulma="panel">
      <LeafletSensorPanelHeading sensor={sensor} />
      <LeafletPanelBody>
        <Section>
          <PanelInfoTable rows={rows} />
        </Section>
        <Section>
          {SelectedPrecipSensor.is(selected) ? (
            <PrecipDataTable selected={selected} />
          ) : (
            <StreamDataTable selected={selected} />
          )}
        </Section>
        <Section>
          {!config.hideRecentDataLink && (
            <TabularDataAnchor onClick={() => setSensor(sensor, "tabular")} />
          )}
        </Section>
        <Section>
          <Div style={{ height: "200px", width: "100%" }}>
            <Chart selected={selected} />
          </Div>
        </Section>
        <Section bulma="is-hidden-touch">
          <ChartAnchor onClick={() => setSensor(sensor, "chart")} />
        </Section>
      </LeafletPanelBody>
    </Div>
  );
};

export default observer(SensorInfoPanel);
